<template>
    <div class="space-y-6">
        <div class="flex items-center justify-between">
            <div>
                <h1 class="text-2xl font-semibold text-gray-900">
                    Manage Assets
                </h1>

                <p class="mt-1 text-sm text-gray-500">
                    Create and manage Lite Integration assets.
                </p>
            </div>

            <button
                type="button"
                class="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                @click="openCreateModal"
            >
                Add Asset
            </button>
        </div>

        <div class="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div
                v-if="loading"
                class="p-6 text-center text-sm text-gray-500"
            >
                Loading assets...
            </div>

            <div
                v-else-if="!assets.length"
                class="p-6 text-center text-sm text-gray-500"
            >
                No assets found.
            </div>

            <div
                v-else
                class="overflow-x-auto"
            >
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                            >
                                Asset
                            </th>

                            <th
                                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                            >
                                UUID
                            </th>

                            <th
                                class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>

                    <tbody class="divide-y divide-gray-200 bg-white">
                        <tr
                            v-for="asset in assets"
                            :key="asset.uuid"
                        >
                            <td class="px-6 py-4">
                                <div class="flex items-center gap-3">
                                    <img
                                        v-if="asset.image_url"
                                        :src="asset.image_url"
                                        :alt="asset.name"
                                        class="h-12 w-12 rounded object-cover"
                                    >

                                    <div
                                        v-else
                                        class="flex h-12 w-12 items-center justify-center rounded bg-gray-100 text-xs text-gray-400"
                                    >
                                        N/A
                                    </div>

                                    <span class="font-medium text-gray-900">
                                        {{ asset.name }}
                                    </span>
                                </div>
                            </td>

                            <td class="px-6 py-4 text-sm text-gray-500">
                                {{ asset.uuid }}
                            </td>

                            <td class="px-6 py-4 text-right text-sm">
                                <button
                                    type="button"
                                    class="mr-4 font-medium text-indigo-600 hover:text-indigo-900"
                                    @click="openEditModal(asset)"
                                >
                                    Edit
                                </button>

                                <button
                                    type="button"
                                    class="font-medium text-red-600 hover:text-red-900"
                                    @click="confirmDelete(asset)"
                                >
                                    Delete
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div
            v-if="pagination.last_page > 1"
            class="flex items-center justify-between"
        >
            <span class="text-sm text-gray-500">
                Showing {{ pagination.from }}–{{ pagination.to }}
                of {{ pagination.total }}
            </span>

            <div class="flex gap-2">
                <button
                    type="button"
                    class="rounded border px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                    :disabled="pagination.current_page <= 1 || loading"
                    @click="loadAssets(pagination.current_page - 1)"
                >
                    Previous
                </button>

                <button
                    type="button"
                    class="rounded border px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                    :disabled="
                        pagination.current_page >= pagination.last_page
                        || loading
                    "
                    @click="loadAssets(pagination.current_page + 1)"
                >
                    Next
                </button>
            </div>
        </div>

        <div
            v-if="showModal"
            class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        >
            <div class="w-full max-w-lg rounded-lg bg-white p-6">
                <h2 class="mb-6 text-lg font-semibold">
                    {{ editingAsset ? 'Edit Asset' : 'Add Asset' }}
                </h2>

                <form
                    class="space-y-4"
                    @submit.prevent="saveAsset"
                >
                    <div>
                        <label
                            for="asset-name"
                            class="mb-1 block text-sm font-medium text-gray-700"
                        >
                            Name
                        </label>

                        <input
                            id="asset-name"
                            v-model.trim="form.name"
                            type="text"
                            required
                            class="w-full rounded-md border border-gray-300 px-3 py-2"
                        >

                        <p
                            v-if="errors.name"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.name[0] }}
                        </p>
                    </div>

                    <div>
                        <label
                            for="asset-image"
                            class="mb-1 block text-sm font-medium text-gray-700"
                        >
                            Image
                        </label>

                        <div
                            v-if="imagePreview"
                            class="mb-3"
                        >
                            <img
                                :src="imagePreview"
                                :alt="form.name || 'Asset image'"
                                class="h-32 w-32 rounded-md border border-gray-200 object-cover"
                            >
                        </div>

                        <input
                            id="asset-image"
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            class="block w-full text-sm text-gray-500 file:mr-4 file:rounded-md file:border-0 file:bg-gray-100 file:px-4 file:py-2 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-200"
                            @change="selectImage"
                        >

                        <p class="mt-1 text-xs text-gray-500">
                            JPG, PNG or WebP. Maximum 5 MB.
                        </p>

                        <p
                            v-if="errors.image"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.image[0] }}
                        </p>
                    </div>

                    <div class="flex justify-end gap-3 pt-4">
                        <button
                            type="button"
                            class="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium"
                            :disabled="saving"
                            @click="closeModal"
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            class="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                            :disabled="saving"
                        >
                            {{ saving ? 'Saving...' : 'Save' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ManageAssets',

    data() {
        return {
            assets: [],

            loading: false,
            saving: false,

            showModal: false,
            editingAsset: null,

            errors: {},

            pagination: {
                current_page: 1,
                last_page: 1,
                from: 0,
                to: 0,
                total: 0,
            },

            form: {
                name: '',
            },

            selectedImage: null,
            imagePreview: null,
            imagePreviewObjectUrl: null,
        };
    },

    mounted() {
        this.loadAssets();
    },

    beforeUnmount() {
        this.revokeImagePreview();
    },

    methods: {
        async loadAssets(page = 1) {
            if (this.loading) {
                return;
            }

            this.loading = true;

            try {
                const response = await this.$http.get(
                    'manage-assets',
                    {
                        params: {
                            page,
                        },
                    }
                );

                const responseData = response.data ?? {};

                this.assets = responseData.data ?? [];

                this.pagination = {
                    current_page:
                        responseData.meta?.current_page ?? 1,

                    last_page:
                        responseData.meta?.last_page ?? 1,

                    from:
                        responseData.meta?.from ?? 0,

                    to:
                        responseData.meta?.to ?? 0,

                    total:
                        responseData.meta?.total ?? 0,
                };
            } catch (error) {
                this.assets = [];

                console.error(
                    'Failed to load assets:',
                    error
                );
            } finally {
                this.loading = false;
            }
        },

        resetForm() {
            this.revokeImagePreview();

            this.form = {
                name: '',
            };

            this.selectedImage = null;
            this.imagePreview = null;
            this.errors = {};
        },

        openCreateModal() {
            this.editingAsset = null;

            this.resetForm();

            this.showModal = true;
        },

        openEditModal(asset) {
            this.resetForm();

            this.editingAsset = asset;

            this.form = {
                name: asset.name,
            };

            this.imagePreview =
                asset.image_url ?? null;

            this.showModal = true;
        },

        closeModal() {
            this.showModal = false;
            this.editingAsset = null;

            this.resetForm();
        },

        selectImage(event) {
            const [image] = event.target.files;

            if (!image) {
                return;
            }

            this.revokeImagePreview();

            this.selectedImage = image;

            this.imagePreviewObjectUrl =
                URL.createObjectURL(image);

            this.imagePreview =
                this.imagePreviewObjectUrl;

            delete this.errors.image;
        },

        revokeImagePreview() {
            if (!this.imagePreviewObjectUrl) {
                return;
            }

            URL.revokeObjectURL(
                this.imagePreviewObjectUrl
            );

            this.imagePreviewObjectUrl = null;
        },

        async saveAsset() {
            if (this.saving) {
                return;
            }

            this.saving = true;
            this.errors = {};

            try {
                const asset = this.editingAsset
                    ? await this.updateAsset()
                    : await this.createAsset();

                if (this.selectedImage) {
                    await this.uploadImage(asset);
                }

                this.closeModal();

                await this.loadAssets(
                    this.pagination.current_page
                );
            } catch (error) {
                if (error.response?.status === 422) {
                    this.errors =
                        error.response.data.errors ?? {};

                    return;
                }

                console.error(
                    'Failed to save asset:',
                    error
                );
            } finally {
                this.saving = false;
            }
        },

        async createAsset() {
            const response = await this.$http.post(
                'manage-assets',
                {
                    name: this.form.name,
                }
            );

            return response.data.data;
        },

        async updateAsset() {
            const response = await this.$http.put(
                `manage-assets/${this.editingAsset.uuid}`,
                {
                    name: this.form.name,
                }
            );

            return response.data.data;
        },

        async uploadImage(asset) {
            const formData = new FormData();

            formData.append(
                'image',
                this.selectedImage
            );

            await this.$http.post(
                `manage-assets/${asset.uuid}/image`,
                formData
            );
        },

        async confirmDelete(asset) {
            if (!window.confirm(`Delete "${asset.name}"?`)) {
                return;
            }

            try {
                await this.$http.delete(
                    `manage-assets/${asset.uuid}`
                );

                const targetPage =
                    this.assets.length === 1
                    && this.pagination.current_page > 1
                        ? this.pagination.current_page - 1
                        : this.pagination.current_page;

                await this.loadAssets(targetPage);
            } catch (error) {
                console.error(
                    'Failed to delete asset:',
                    error
                );
            }
        },
    },
};
</script>